import axios from 'axios';
import { endpoints } from '../endpoints';

const DEFAULT_ERROR_MESSAGE = 'error.500';
const HTTP_BAD_REQUEST = 400;
const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const HTTP_TIMEOUT = 408;
const HTTP_INTERNAL_SERVER_ERROR = 500;

const API_ERRORS: Record<number, string> = {
    [HTTP_BAD_REQUEST]: 'error.400',
    [HTTP_UNAUTHORIZED]: 'error.401',
    [HTTP_FORBIDDEN]: 'error.403',
    [HTTP_NOT_FOUND]: 'error.404',
    [HTTP_TIMEOUT]: 'error.408',
    [HTTP_INTERNAL_SERVER_ERROR]: 'error.500'
};

interface ApiCallConfig {
    url: string;
    method: 'get' | 'post';
    params?: ApiCallGetParams;
    data?: ApiCallPostParams;
    headers?: Record<string, string>;
}

export interface ApiCallResult<ResponseType> {
    data?: ResponseType;
    status: number;
    error?: string;
}

type ApiCallHeaders = Record<string, string>;

type ApiCallGetParams = Record<string | number, string | string[] | number | number[]>;

type ApiCallPostParams = Record<string | number, any>;

export interface FileUploadResult {
    fileIdentifier: string;
}

export class Api {
    static async get<ResponseType>(url: string, params?: ApiCallGetParams): Promise<ApiCallResult<ResponseType>> {
        return apiCall({
            method: 'get',
            url,
            params
        });
    }

    static async post<ResponseType>(url: string, data?: ApiCallPostParams): Promise<ApiCallResult<ResponseType>> {
        return await apiCall({ method: 'post', url, data });
    }

    static async uploadFile(file: File, headers: ApiCallHeaders): Promise<ApiCallResult<FileUploadResult>> {
        const url = endpoints.files;
        const formData = new FormData();
        formData.append('file', file);

        return apiCall({
            method: 'post',
            url,
            headers,
            data: formData
        });
    }
}

async function apiCall<ResponseType>({
    method,
    url,
    params,
    data: postData,
    headers
}: ApiCallConfig): Promise<ApiCallResult<ResponseType>> {
    try {
        const { data, status }: { data: ResponseType; status: number } = await axios({
            method,
            url,
            params: params,
            data: postData,
            headers
        });

        if (status === 200) {
            return { data, status };
        } else {
            // return error based on the returned http status code
            return { status, error: API_ERRORS[status] || DEFAULT_ERROR_MESSAGE };
        }
    } catch (e) {
        if (!e.response) {
            // handle this as "could not reach the server" error
            console.error(e);
            return { status: 0, error: API_ERRORS[HTTP_TIMEOUT] };
        } else {
            return { status: e.response.status, data: e.response.data };
        }
    }
}
